import { getFrameInfos } from '@microsoft/wv2-outlook/instanceIframeTracker';
import { getSettings } from './getSettings';
import { trace } from 'owa-trace';
import { iframesMapping, type IframeScenario } from '../settings/iframes-mapping';
import type { IframeState } from '../hooks/useIframeState';
import { checkIfMemoryIsAboveThreshold } from '../hooks/useIframeMemoryManagement';

let interval: NodeJS.Timeout | null = null;
const listeners = new Map<
    string,
    {
        scenario: IframeScenario;
        state: IframeState;
        callback: (memory: number | undefined, isAboveThreshold: boolean) => void;
    }
>();

export function registerIframeMemoryListener(
    id: string,
    scenario: IframeScenario,
    state: IframeState,
    callback: (memory: number | undefined, isAboveThreshold: boolean) => void
): void {
    trace.info(` Iframe memory listener registered: ${id} (${scenario})`, 'iframeWrapper');

    // Set the callback listener
    listeners.set(id, { scenario, state, callback });

    // Set the interval to check the memory health
    if (!interval) {
        interval = setInterval(() => checkMemoryHealth(), getSettings().backgroundCheckInterval);

        // Run the check for the first time
        checkMemoryHealth();
    }
}

export function unregisterIframeMemoryListener(id: string): void {
    trace.info(` Iframe memory listener unregistered: ${id}`, 'iframeWrapper');

    listeners.delete(id);

    if (listeners.size === 0 && interval) {
        clearInterval(interval);
        interval = null;
    }
}

async function checkMemoryHealth() {
    const memoryBySrc = await getIframeMemoryByScenario();

    trace.info(
        ` Iframe memory health checked: ${JSON.stringify(Array.from(memoryBySrc.entries()))}`,
        'iframeWrapper'
    );

    for (const [, { scenario, state, callback }] of listeners) {
        const memory = memoryBySrc.get(scenario);
        const isAboveThreshold = checkIfMemoryIsAboveThreshold(memory, state) ?? false;

        callback(memory, isAboveThreshold);
    }
}

export async function getIframeMemoryByScenario(): Promise<Map<string, number>> {
    // Outside of Monarch, don't try to read the memory info
    if (!getFrameInfos) {
        return new Map<string, number>();
    }

    try {
        const allIframeSettings = Object.entries(iframesMapping);
        const iframes = await getFrameInfos?.();
        const map = new Map<string, number>();

        trace.info(` Iframe memory info retrieved: ${JSON.stringify(iframes)}`, 'iframeWrapper');

        for (const { iframeSource: src, workingSetSize: workingSet } of iframes) {
            const match = allIframeSettings.find(([, settings]) => settings.matchUrl(src));

            if (!match) {
                trace.warn(
                    ` Iframe with source ${src} does not match any known scenario`,
                    'iframeWrapper'
                );
                continue;
            }

            if (src && Number.isFinite(workingSet)) {
                map.set(match[0], workingSet as number);
            }
        }

        return map;
    } catch {
        // no-op: this may fail on the web
        return new Map<string, number>();
    }
}
